<template>
  <b-col class="mt-4 mt-xl-0 device-data panel panel--tabbed">
    <loader
      :watch="[
        'visnetHubFaultPanelData',
        'visnetHubFaultElectricalData',
        'visnetHubFaultElectricalDataImpedance'
      ]"
    >
      <b-tabs>
        <b-tab :key="tabsNames[0]" :title="tabsNames[0]">
          <template v-if="isDataPresent">
            <b-row class="justify-content-around pb-2">
              <div v-if="eventTimestamp" class="mr-1 visnet-events__fault-impedance">
                Selected Event Time:
              </div>
              <div v-else class="mr-1 visnet-events__fault-impedance">Last Time Updated:</div>
              <div>{{ faultElectricalData.lastMeasurementTime | formatDate }}</div>
            </b-row>
            <b-row class="justify-content-center visnet-events__updated-row">
              <b-col
                v-for="columnNumber in 3"
                :key="columnNumber"
                class="p-2 mx-2 text-center"
                cols="3"
              >
                <span class="visnet-events__fault-headers">L{{ columnNumber }}</span>
              </b-col>
            </b-row>
            <b-row
              v-for="(phase, phaseKey) in faultElectricalData.electricalValues"
              :key="phaseKey"
              class="justify-content-center"
            >
              <b-col
                v-for="attributeValue in phase"
                :key="`${phaseKey}${attributeValue.phaseKey}`"
                class="d-flex flex-column justify-content-between align-items-center text-center p-2 mx-2"
                cols="3"
              >
                <span class="visnet-events__fault-labels">
                  {{ mappedNames[phaseKey] }}
                </span>
                <span class="visnet-events__fault-values">{{ attributeValue.value || '-' }}</span>
              </b-col>
            </b-row>
            <b-row class="justify-content-end">
              <b-button variant="rounded" @click="fetchTableData">
                <span>Fetch Defaults</span>
                <Icon name="download" />
              </b-button>
            </b-row>
          </template>
          <template v-else>
            <b-row>No data present for given timespan</b-row>
            <b-row v-if="eventTimestamp || !isDataPresent" class="mt-3 justify-content-end">
              <b-button variant="rounded" @click="resetIntervalDefaultFetch">
                <span>Fetch Defaults</span>
                <Icon name="download" />
              </b-button>
            </b-row>
          </template>
        </b-tab>
        <b-tab :key="tabsNames[1]" :title="tabsNames[1]">
          <template v-if="faultElectricalDataImpedance.createdAt">
            <div class="visnet-events__fault-impedance justify-content-center pb-2">
              Best impedance:
            </div>
            <ShowAttribute
              v-for="(value, key) in changeDateFormatAndRepackage(
                faultElectricalDataImpedance.bestImpedance
              )"
              :key="`best${key}`"
              :value="value"
              :label="key"
            />
            <div class="visnet-events__fault-impedance justify-content-center pb-2">
              Latest impedance:
            </div>
            <ShowAttribute
              v-for="(value, key) in changeDateFormatAndRepackage(
                faultElectricalDataImpedance.latestImpedance
              )"
              :key="`latest${key}`"
              :value="value"
              :label="key"
            />
          </template>
          <template v-else>
            <b-row>No Impedance Data</b-row>
          </template>
        </b-tab>
      </b-tabs>
    </loader>
  </b-col>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import MomentTimezone from 'moment-timezone';
import ShowAttribute from '@/components/device/show-attribute';
import { FORMAT_YMD_HHMMSS, FORMAT_YMD_TIMEZONE } from '@/components/shared/date-format';

const MAPPED_NAMES = {
  voltage: 'Busbar Voltage',
  currentRms: 'Current RMS',
  voltageRms: 'Cable Voltage'
};
const TABS_NAMES = ['Data', 'Impedance'];
const EVERY_3_MIN_INTERVAL_IN_MS = 3 * 60 * 1000;
const TIMEOUT_STATES = {
  INITIATE: 'initiate',
  RENEW: 'renew',
  STOP: 'stop'
};

export default {
  name: 'FaultDataTable',
  components: { ShowAttribute },
  filters: {
    formatDate(value) {
      if (!value) return 'No time provided';
      return MomentTimezone(value).format(FORMAT_YMD_HHMMSS);
    }
  },
  data() {
    return {
      tabIndex: 0,
      mappedNames: MAPPED_NAMES,
      tabsNames: TABS_NAMES,
      intervalID: null
    };
  },
  computed: {
    ...mapState({
      faultElectricalData: state => {
        const {
          id,
          type,
          lastMeasurementTime,
          ...electricalDataRest
        } = state.visnetHubFaultElectricalData.data;

        const electricalValues = Object.keys(electricalDataRest).reduce(
          (reducer, phaseKey) => {
            Object.keys(electricalDataRest[phaseKey]).forEach(electricalValueKey => {
              if (!electricalDataRest[phaseKey][electricalValueKey]) {
                return reducer[electricalValueKey].push({ timestamp: null, value: '-', phaseKey });
              }
              return reducer[electricalValueKey].push({
                ...electricalDataRest[phaseKey][electricalValueKey],
                phaseKey
              });
            });

            return reducer;
          },
          { currentRms: [], voltage: [], voltageRms: [] }
        );

        return { lastMeasurementTime, electricalValues };
      },
      faultElectricalDataImpedance: state => state.visnetHubFaultElectricalDataImpedance.data,
      eventTimestamp: state => state.visnetHubFaultWaveforms.eventTimestamp
    }),
    isDataPresent() {
      const { currentRms, voltage, voltageRms } = this.faultElectricalData.electricalValues;

      return Boolean(currentRms.length || voltage.length || voltageRms.length);
    }
  },
  watch: {
    eventTimestamp(newEvent, oldEvent) {
      if (!oldEvent && newEvent) {
        this.intervalStateHandler(TIMEOUT_STATES.STOP);
      } else if (oldEvent && !newEvent) {
        this.fetchTableData();

        this.intervalStateHandler(TIMEOUT_STATES.INITIATE);
      }
    }
  },
  mounted() {
    const {
      externalVisnetHubId,
      projectCode,
      substationCode,
      feederNumber,
      phaseNumber
    } = this.$route.params;

    const impedanceDataPayload = {
      externalVisnetHubId,
      projectCode,
      substationCode,
      feederNumber,
      phaseNumber
    };

    this.fetchImpedanceData(impedanceDataPayload);

    this.fetchTableData();

    this.intervalStateHandler(TIMEOUT_STATES.INITIATE);
  },
  beforeDestroy() {
    this.intervalStateHandler(TIMEOUT_STATES.STOP);
  },
  methods: {
    ...mapActions({
      fetchFaultElectricalData: 'visnetHubFaultElectricalData/fetchFaultElectricalData',
      fetchImpedanceData: 'visnetHubFaultElectricalDataImpedance/fetchFaultElectricalDataImpedance'
    }),
    ...mapMutations({
      setActiveEventTimestamp: 'visnetHubFaultWaveforms/setActiveEventTimestamp'
    }),
    intervalStateHandler(state) {
      clearInterval(this.intervalID);

      this.intervalID = null;

      if ([TIMEOUT_STATES.INITIATE, TIMEOUT_STATES.RENEW].includes(state)) {
        this.intervalID = setInterval(() => {
          this.fetchTableData();
        }, EVERY_3_MIN_INTERVAL_IN_MS);
      }
    },
    resetIntervalDefaultFetch() {
      this.setActiveEventTimestamp(null);
    },
    fetchTableData() {
      const { externalVisnetHubId, projectCode, substationCode, feederNumber } = this.$route.params;

      const electricalDataPayload = {
        externalVisnetHubId,
        projectCode,
        substationCode,
        feederNumber
      };

      this.fetchFaultElectricalData(electricalDataPayload);
    },
    changeDateFormatAndRepackage(object) {
      const { date, ...rest } = object;

      const dateFormatted = date ? MomentTimezone(date * 1000).format(FORMAT_YMD_TIMEZONE) : '-';

      return { date: dateFormatted, ...rest };
    }
  }
};
</script>
